/**
 * RouteDisplay - Shared route visualization component
 *
 * Renders every stop on the route with connected dots and compact appointment
 * times. A route is however many stops the dispatcher put on it — a live-load
 * with two warehouses is four stops, and all four are drawn.
 *
 * This used to draw exactly two nodes and fold the return leg into a ⇌
 * connector, which meant a multi-stop route silently read as a two-stop one:
 * a live-load through two warehouses showed "SSA → SSA" with both warehouses
 * missing. Stops come from route_nodes now (the source of truth), with the
 * legacy origin/destination fields only as a fallback for rows that predate it.
 */

import React from 'react'
import { Package } from 'lucide-react';
import { ContainerTask, LocationType, RouteNode } from '../../types/container';
import { formatApptDateTime } from '../../utils/apptFormat';
import { isPastApptDay } from '../../utils/apptSchedule';
import { generateRouteNodes } from '../../utils/routeGenerator';

const locationDotColor: Record<string, string> = {
  port: 'bg-blue-500',
  warehouse: 'bg-green-500',
  yard: 'bg-amber-500',
  pcc: 'bg-red-500',
  shipper: 'bg-violet-500',
  custom: 'bg-slate-400',
}

function resolveAppt(dateStr: string | undefined, status?: string, allDay?: boolean): string | undefined {
  // 取消要盖过时间。码头把票作废之后，行里那个时间还在（留着是为了让人知道
  // 被取消的是哪一张、原定几点），但屏幕上再显示成一个绿色的时间，就等于告诉
  // 调度「约着呢」——2026-09-14 ETS 一夜取消 4 张，板子上照旧显示 9/15 18:00。
  if (status === 'cancelled') return 'cancelled'
  if (dateStr && dateStr !== 'Pending') {
    const formatted = formatApptDateTime(dateStr, allDay)
    if (formatted) return formatted
  }
  if (dateStr === 'Pending' || status === 'pending') return 'pending'
  return undefined
}

/**
 * 这个已经排出来的预约要不要标红。
 *
 * 只对真的排出了时间的那种成立 —— 'pending' 是还没约上（琥珀色），'nar' 是这一站
 * 本来就不用约（灰色），两者都不存在「过期」这回事。
 */
function apptIsPast(appt: string | undefined, raw?: string | null): boolean {
  if (!appt || appt === 'pending' || appt === 'nar' || appt === 'cancelled') return false
  return isPastApptDay(raw)
}

function resolveShortName(task: ContainerTask, type?: LocationType, fallbackName?: string): string {
  switch (type) {
    case 'port':
      return task.portShortName || task.portName || fallbackName || 'TBD'
    case 'warehouse':
      return task.warehouseShortName || task.warehouseName || task.warehouse || fallbackName || 'TBD'
    case 'yard':
      return task.yardShortName || task.yardName || fallbackName || 'TBD'
    case 'pcc':
      return task.pccLocationName || fallbackName || 'PCC'
    case 'shipper':
      return task.shipperName || task.shipper || fallbackName || 'TBD'
    default:
      return fallbackName || 'TBD'
  }
}

interface NodeInfo { name: string; fullName: string; type: LocationType; appt?: string; apptPast?: boolean; city?: string; address?: string }

/** Route 列空间有限：后端给的是 "City, ST"，行内只保留 city 部分（州放在 hover 提示里） */
function cityLabel(city?: string): string | undefined {
  const first = city?.split(',')[0]?.trim()
  return first || undefined
}

function resolvePortAppt(dateStr: string | undefined, status?: string, allDay?: boolean): string {
  return resolveAppt(dateStr, status, allDay) || 'pending'
}

function getOrigin(task: ContainerTask): NodeInfo {
  const type = task.originType || 'port'
  // Use origin-specific fields first; only fall back to task-level port/warehouse
  // when origin matches the task's primary association (prevents Street Turn
  // showing task.warehouseName instead of the actual origin warehouse name)
  const fullName = task.originName || (type === 'port' ? task.portName : '') || ''
  const name = task.originShortName || resolveShortName(task, type, task.originName)
  let appt: string | undefined
  let rawAppt: string | undefined
  if (type === 'port') {
    // 起点是码头 = 这一程就是提柜。票废了就显示 Rebook（见 stopAppt 同款判断）。
    appt = task.portApptNeedsRebook
      ? 'rebook'
      : resolvePortAppt(task.portAppointment, task.portAppointmentStatus, task.portAppointmentAllDay)
    rawAppt = task.portAppointment
  } else if (type === 'warehouse') {
    // Check if origin warehouse needs appointment:
    // - routeNodes explicit flag takes priority
    // - empty-in / return tasks: warehouse pickup doesn't require appointment
    const pickNode = Array.isArray(task.routeNodes)
      ? task.routeNodes.find(n => n.action === 'pick' && n.type === 'warehouse')
      : undefined
    const noApptRequired = pickNode?.appointmentRequired === false
      || (pickNode?.appointmentRequired === undefined && ['empty-in', 'return'].includes(task.taskType))
    if (noApptRequired) {
      appt = 'nar'
    } else {
      appt = resolveAppt(task.warehouseAppointment, task.warehouseAppointmentStatus, task.warehouseAppointmentAllDay)
      rawAppt = task.warehouseAppointment
    }
  }
  return {
    name,
    fullName,
    type,
    appt,
    apptPast: apptIsPast(appt, rawAppt),
    city: task.originCity,
    address: task.originAddress,
  }
}

function getDestination(task: ContainerTask): NodeInfo {
  const type = task.destinationType || 'warehouse'
  // Use destination-specific fields first; prevents showing task-level warehouse
  // name when destination is a different warehouse
  const fullName = task.destinationName || (type === 'warehouse' ? task.warehouseName || task.warehouse : '') || ''
  const name = task.destinationShortName
    || (task.destinationType === 'port' ? (task.portShortName || task.portName || 'TBD')
      : task.destinationType === 'yard' ? (task.yardShortName || task.yardName || 'TBD')
      : resolveShortName(task, type, task.destinationName))
  let appt: string | undefined
  let rawAppt: string | undefined
  if (type === 'warehouse') {
    // Check routeNodes for appointmentRequired flag on destination warehouse
    const destNode = Array.isArray(task.routeNodes)
      ? task.routeNodes.find(n => n.type === 'warehouse' && n.action !== 'pick')
      : undefined
    if (destNode?.appointmentRequired === false) {
      appt = 'nar'
    } else {
      appt = resolveAppt(task.warehouseAppointment, task.warehouseAppointmentStatus, task.warehouseAppointmentAllDay)
      rawAppt = task.warehouseAppointment
    }
  } else if (type === 'port') {
    // Find destination port node — include 'return' action (e.g., empty-in tasks)
    const destNode = task.routeNodes && Array.isArray(task.routeNodes)
      ? task.routeNodes.find(n => n.action !== 'pick' && n.type === 'port')
      : undefined
    appt = resolvePortAppt(destNode?.appointmentTime, destNode?.appointmentStatus, destNode?.appointmentAllDay)
    rawAppt = destNode?.appointmentTime
  }
  return {
    name,
    fullName,
    type,
    appt,
    apptPast: apptIsPast(appt, rawAppt),
    city: task.destinationCity,
    address: task.destinationAddress,
  }
}

/** Sentinel that getOrigin/getDestination fall back to when nothing resolves. */
const UNRESOLVED = 'TBD'

export interface RouteEndpoint {
  /** Short name for compact display; '' when the node can't be resolved */
  shortName: string
  fullName: string
  type: LocationType
  /** Raw "City, ST" as the backend sends it; use cityName() for a compact label */
  city?: string
  /** Formatted appointment, or the 'pending' / 'nar' / 'rebook' sentinels; see resolveAppt */
  appt?: string
  /** 预约日期已经比今天早 —— 时间标红，提醒这一站已经错过 */
  apptPast?: boolean
  /** Stop the driver never made — rendered struck through */
  rejected?: boolean
}

/** "City, ST" trimmed to just the city — for rows that can't spare the width. */
export function cityName(city?: string): string | undefined {
  return cityLabel(city)
}

/**
 * Origin/destination short names without the appointment/dot chrome — for the
 * compact route line on task cards (driver cards, confirmation drawer).
 * Reuses the same resolution the Route column uses so the short names always
 * agree with the table.
 *
 * `appt` rides along per endpoint rather than as a port/warehouse pair: which
 * appointment belongs to a node depends on that node's type and the task
 * direction (an empty-in starts at the warehouse), so callers that pair them up
 * positionally get it backwards on half the task types.
 */
export function getRouteEndpoints(task: ContainerTask): { origin: RouteEndpoint; destination: RouteEndpoint } {
  const origin = getOrigin(task)
  const destination = getDestination(task)
  return {
    origin: {
      shortName: origin.name === UNRESOLVED ? '' : origin.name,
      fullName: origin.fullName || origin.name,
      type: origin.type,
      city: origin.city,
      appt: origin.appt,
      apptPast: origin.apptPast,
    },
    destination: {
      shortName: destination.name === UNRESOLVED ? '' : destination.name,
      fullName: destination.fullName || destination.name,
      type: destination.type,
      city: destination.city,
      appt: destination.appt,
      apptPast: destination.apptPast,
    },
  }
}

/**
 * Every stop on the route, in order, in the compact endpoint shape.
 *
 * The card views used to take only `{ origin, destination }` from
 * `getRouteEndpoints`, which is exactly two stops however many the route has —
 * a live-load through two warehouses lost both of them. Callers that render a
 * route should walk this instead; `getRouteEndpoints` remains for the places
 * that genuinely only want the two ends.
 *
 * Falls back to the two endpoints when a task has no usable route nodes, so a
 * caller always gets at least the shape it had before.
 */
export function getRouteStops(task: ContainerTask): RouteEndpoint[] {
  const stops = buildRouteStops(task)
  if (stops.length === 0) {
    const { origin, destination } = getRouteEndpoints(task)
    return [origin, destination]
  }
  return stops.map(stop => ({
    shortName: stop.name === UNRESOLVED ? '' : stop.name,
    fullName: stop.fullName || stop.name,
    type: stop.type,
    city: stop.city,
    appt: stop.appt,
    apptPast: stop.apptPast,
    rejected: stop.rejected,
  }))
}

const apptLocationTypes = new Set<string>(['port', 'warehouse'])

/** Determine which node index is "current" based on task status */
function getCurrentNodeIndex(task: ContainerTask, nodeCount: number): number {
  const s = task.status
  if (s === 'completed' || s === 'cancelled') return nodeCount // all done
  if (s === 'delivering' || s === 'on-stop' || s === 'loading') return 1
  if (s === 'returning') return nodeCount - 1
  return 0
}

/** One stop as the row draws it. */
interface RouteStop {
  name: string
  fullName: string
  type: LocationType
  appt?: string
  /** 预约日期已经比今天早 —— 时间标红 */
  apptPast?: boolean
  city?: string
  address?: string
  /** Skipped stop (warehouse refused the load) — drawn struck through */
  rejected: boolean
  /** Driver is done with this stop; drives the solid-vs-dashed connector */
  done: boolean
  /** Where the driver is now — the one pulsing dot */
  isCurrent: boolean
}

/**
 * Appointment label for a stop.
 *
 * Mirrors what the endpoint helpers do, with one addition the old two-node
 * render never had to make: a task carries a single flat `warehouse_appointment`
 * column, so on a route with more than one warehouse it can only be trusted for
 * the warehouse the task itself points at. Every other warehouse stop shows an
 * em-dash rather than borrowing an appointment that isn't its own.
 */
function stopAppt(task: ContainerTask, node: RouteNode): { appt?: string; past: boolean } {
  const of = (appt: string | undefined, raw?: string | null) => ({ appt, past: apptIsPast(appt, raw) })

  if (node.appointmentRequired === false) return { appt: 'nar', past: false }
  if (node.type === 'port') {
    // 这一程的票已经跑不成了 —— 显示 Rebook，不显示那个时间。时间还在那里
    // 只会让人以为这趟还成立（封闭区解除时服务端判的，见 migration 191；
    // 2026-09-20 起还包括「票号和时间对不上」判出来的假票）。
    //
    // 🔴 按**这一站自己的方向**对，不是写死 'pick'。
    // Live Load 一张任务两张票，写死 pick 的话，还柜那张作废了界面一点都看不出来 ——
    // 照旧显示「Scheduled + 时间」，而那张票在码头上根本不存在（线上真发生过）。
    const side = node.action === 'return' ? 'drop' : 'pick'
    const sides = task.portApptRebookSides
    const needsRebook = sides && sides.length > 0
      ? sides.includes(side)
      // 后端还没回新字段时退回老行为（只认提柜那一程），别把别的站误标成 Rebook
      : (node.action === 'pick' && !!task.portApptNeedsRebook)
    if (needsRebook) return { appt: 'rebook', past: true }
    return of(resolvePortAppt(node.appointmentTime, node.appointmentStatus, node.appointmentAllDay), node.appointmentTime)
  }
  if (node.type !== 'warehouse') return { appt: undefined, past: false }

  const ownsTaskAppointment = !node.id || !task.warehouseId || node.id === task.warehouseId
  if (node.appointmentTime && (node.appointmentNumber || ownsTaskAppointment)) {
    return of(resolveAppt(node.appointmentTime, node.appointmentStatus, node.appointmentAllDay), node.appointmentTime)
  }
  return ownsTaskAppointment
    ? of(
        resolveAppt(task.warehouseAppointment, task.warehouseAppointmentStatus, task.warehouseAppointmentAllDay),
        task.warehouseAppointment
      )
    : { appt: undefined, past: false }
}

/** Whether the task carries a real route to draw, in either wire shape. */
function hasRouteNodes(task: ContainerTask): boolean {
  const raw = task.routeNodes ?? (task as unknown as Record<string, unknown>).route_nodes
  if (Array.isArray(raw)) return raw.length > 0
  if (typeof raw === 'string') return raw.trim().length > 0 && raw.trim() !== '[]'
  return false
}

/**
 * The stops to draw, in order.
 *
 * Only tasks that actually carry route_nodes go down this path. `generateRouteNodes`
 * will happily rebuild a route from the flat columns when they're missing, but its
 * reconstruction disagrees with the endpoint helpers on some task types — an
 * export Pick & Drop came back with the warehouse as both ends — and rows without
 * route_nodes are precisely the ones with no second opinion available. They keep
 * the endpoint rendering they have always had; returning [] here selects it.
 *
 * (Both dev and prod currently have zero such rows, so this is a guard against a
 * legacy row reappearing, not a path anyone is on today.)
 */
function buildRouteStops(task: ContainerTask): RouteStop[] {
  if (!hasRouteNodes(task)) return []
  const nodes = generateRouteNodes(task)
  if (nodes.length === 0) return []

  const fallbackIndex = getCurrentNodeIndex(task, nodes.length)
  const inProgress = nodes.findIndex(n => n.status === 'in-progress')
  const activeIndex = inProgress >= 0
    ? inProgress
    : (fallbackIndex >= nodes.length ? -1 : fallbackIndex)

  return nodes.map((node, i) => {
    const rejected = node.action === 'rejected' || node.status === 'rejected'
    const { appt, past } = rejected ? { appt: undefined, past: false } : stopAppt(task, node)
    return {
      name: node.name || UNRESOLVED,
      fullName: node.fullName || node.name || UNRESOLVED,
      type: node.type,
      appt,
      apptPast: past,
      city: node.city,
      address: node.address,
      rejected,
      // A stop counts as done once the driver has moved past it: either the
      // backend says so, or it sits behind wherever they are now.
      done: node.status === 'completed' || (activeIndex >= 0 && i < activeIndex),
      isCurrent: i === activeIndex,
    }
  })
}

function NodeDot({ type, size, isCurrent }: { type: LocationType; size: number; isCurrent: boolean }) {
  const bg = locationDotColor[type] || locationDotColor.custom
  return (
    <span
      className={`inline-block rounded-full flex-shrink-0 ${bg} ${isCurrent ? 'animate-pulse ring-2 ring-blue-200' : ''}`}
      style={{ width: size, height: size }}
    />
  )
}

/**
 * Half-arrow (harpoon) connector between two route stops.
 *
 * One barb per leg, always pointing forward — the route is drawn stop by stop
 * now, so a live-load's return leg is a connector like any other instead of a
 * ⇌ standing in for a stop that wasn't drawn. Completed legs are solid blue,
 * upcoming legs stay dashed grey.
 *
 * Narrower than it used to be: a four-stop route has three of these, and the
 * Route column has to stay readable.
 */
function Connector({ completed, title }: { completed: boolean; title?: string }) {
  const stroke = completed ? '#60a5fa' : '#cbd5e1' // blue-400 / slate-300
  const dash = completed ? undefined : '3 2'
  const barb = { stroke, strokeWidth: 1.5, fill: 'none', strokeLinecap: 'round' as const }
  return (
    <svg
      width="20" height="12" viewBox="0 0 20 12"
      className="flex-shrink-0 self-center" role="img"
      aria-label={title || 'To'}
    >
      {title && <title>{title}</title>}
      <line x1="1" y1="6" x2="19" y2="6" strokeDasharray={dash} {...barb} />
      <path d="M19 6 L14 3.2" {...barb} />
    </svg>
  )
}

function LocationNode({
  name,
  type,
  fullName,
  appt,
  apptPast,
  city,
  address,
  isCurrent,
  rejected,
}: {
  name: string
  type: LocationType
  fullName?: string
  appt?: string
  apptPast?: boolean
  city?: string
  address?: string
  isCurrent: boolean
  /** Stop the driver never made — struck through so it reads as skipped, not visited */
  rejected?: boolean
  nodeIndex?: number
  currentIndex?: number
}) {
  const dotSize = isCurrent ? 8 : 6
  // City sits inline after the name so the row height never grows; full
  // "City, ST" + street address stay in the hover tooltip.
  const shortCity = cityLabel(city)
  const tooltip = [fullName || name, address, city].filter(Boolean).join(' — ')
  return (
    <span className="inline-flex flex-col min-w-0" title={tooltip}>
      <span className="inline-flex items-center gap-1 min-w-0">
        <NodeDot type={rejected ? 'custom' : type} size={dotSize} isCurrent={isCurrent} />
        {/* Some facility names are a whole address ("RAMAR INTERNATIONAL CORP(fish)
            335 Central Ave Pittsburg, CA 94565") and would push the Route column
            over the rest of the row. truncate alone doesn't bite without a width
            cap — the flex parent just grows. The cap is sized to roughly the
            first three words ("RAMAR INTERNATIONAL CORP"); the full name stays
            in the hover tooltip. */}
        <span className={`font-medium truncate max-w-[180px] ${
          rejected ? 'text-slate-400 line-through' : isCurrent ? 'text-blue-700' : 'text-slate-900'
        }`}>{name}</span>
        {shortCity && (
          <span className="text-slate-400 font-normal truncate">&middot; {shortCity}</span>
        )}
      </span>
      {appt === 'nar' ? (
        <span className="text-xs text-slate-400 leading-snug pl-3">NAR</span>
      ) : appt === 'rebook' ? (
        // 票废了。这里**不显示原来那个时间** —— 一个具体的时间会让人以为这趟
        // 还成立，而它已经跑不成了（封闭区解除时只剩不到两小时或已过时）。
        <span
          className="text-xs font-bold leading-snug pl-3 text-red-600"
          title="Closed area lifted too late for this slot — book a new appointment"
        >Rebook</span>
      ) : appt === 'cancelled' ? (
        // 比 Pending 重一级：Pending 是「还没约」，这个是「约过了、码头作废了」，
        // 本来占着的位子没了，得有人立刻去重约。
        <span
          className="text-xs font-bold text-red-600 leading-snug pl-3"
          title="The terminal cancelled this appointment — rebook it"
        >CANCELED</span>
      ) : appt === 'pending' ? (
        <span className="text-xs text-amber-500 leading-snug pl-3">Pending</span>
      ) : appt ? (
        // 预约时间是这一行最该被第一眼看到的东西——加粗 + 比其它辅助文字大一号。
        // 绿色=约还在前面；红色=日期已经过了，这一站已经错过，得有人去改约。
        <span
          className={`text-xs font-bold leading-snug pl-3 ${apptPast ? 'text-red-600' : 'text-emerald-600'}`}
          title={apptPast ? 'Appointment date has passed' : undefined}
        >{appt}</span>
      ) : apptLocationTypes.has(type) ? (
        <span className="text-xs text-slate-300 leading-snug pl-3">&mdash;</span>
      ) : null}
    </span>
  )
}

function DistanceBadge({ miles }: { miles?: number }) {
  if (!miles) return null
  return (
    <span className="ml-1 self-start mt-[3px] inline-flex items-center px-1 py-0 rounded text-[10px] font-medium bg-slate-100 text-slate-500 flex-shrink-0" title={`Estimated ${miles} miles`}>
      ~{miles < 10 ? miles.toFixed(1) : Math.round(miles)} mi
    </span>
  )
}

function BobtailBadge() {
  return (
    <span className="ml-1 self-start mt-[3px] inline-flex items-center px-1 py-0 rounded text-[10px] font-semibold bg-orange-100 text-orange-600 flex-shrink-0" title="Bobtail leg (cross-zone)">
      BT
    </span>
  )
}

/** Yard-storage reminder: who pulled this container to yard on leg1 — assign the
 *  delivery back to them. Inline (self-start) so it never adds row height. */
function PulledByBadge({ driverName }: { driverName: string }) {
  return (
    <span
      className="ml-1 self-start mt-[2px] inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-xs font-medium bg-indigo-50 text-indigo-600 flex-shrink-0 whitespace-nowrap"
      title={`Pulled to yard by ${driverName} — assign this delivery back to them to keep the yard-storage through rate fair`}
    >
      <Package className="w-3.5 h-3.5 flex-shrink-0" aria-hidden="true" /> {driverName}
    </span>
  )
}

export function RouteDisplay({ task, leg1DriverName }: { task: ContainerTask; leg1DriverName?: string }) {
  const stops = React.useMemo(() => buildRouteStops(task), [task])
  const displayMiles = task.actualDistanceMiles || task.estimatedDistanceMiles

  // Endpoint helpers still resolve the two ends better than a bare node does
  // (they know the task's own port/warehouse fields), so a route that produced
  // no nodes at all falls back to them rather than rendering an empty cell.
  if (stops.length === 0) {
    const origin = getOrigin(task)
    const dest = getDestination(task)
    return (
      <div className="flex items-baseline gap-0.5 flex-wrap min-w-0 text-xs">
        <LocationNode
          name={origin.name} type={origin.type} fullName={origin.fullName} appt={origin.appt}
          apptPast={origin.apptPast} city={origin.city} address={origin.address} isCurrent={false}
        />
        <Connector completed={false} />
        <LocationNode
          name={dest.name} type={dest.type} fullName={dest.fullName} appt={dest.appt}
          apptPast={dest.apptPast} city={dest.city} address={dest.address} isCurrent={false}
        />
        <DistanceBadge miles={displayMiles} />
      </div>
    )
  }

  return (
    <div className="flex items-baseline gap-0.5 flex-wrap min-w-0 text-xs">
      {stops.map((stop, i) => (
        <React.Fragment key={`${stop.type}-${stop.name}-${i}`}>
          {i > 0 && (
            <Connector
              // The leg into this stop is done once the stop itself is behind
              // the driver — same signal the single connector used to carry.
              completed={stop.done || stop.isCurrent}
              title={`To ${stop.fullName || stop.name}`}
            />
          )}
          <LocationNode
            name={stop.name} type={stop.type} fullName={stop.fullName} appt={stop.appt}
            apptPast={stop.apptPast} city={stop.city} address={stop.address}
            isCurrent={stop.isCurrent} rejected={stop.rejected}
          />
        </React.Fragment>
      ))}
      <DistanceBadge miles={displayMiles} />
      {task.isBobtailLeg && <BobtailBadge />}
      {leg1DriverName && task.taskType === 'delivery' && <PulledByBadge driverName={leg1DriverName} />}
    </div>
  )
}
